/*
 * Created Date: Sunday April 3rd 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import React, { ReactNode } from "react";
import { Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Grid } from "@mui/material";
import ToolTip from "./ToolTip";
import { InfoOutlined } from "@mui/icons-material";

export interface CustomToggleButtonProps {
  onChange: (event: React.MouseEvent<HTMLElement>, value: any) => void;
  value: string | any;
  buttons: {
    value: any;
    label: string;
    startIcon?: any;
    disabled?: boolean;
    disabledInfo?: string;
    endIcon?: any;
    infoContent?: ReactNode;
    id?: string;
  }[];
  style?: any;
  align?: string;
  size?: "medium" | "large";
}

const CustomToggleButton = ({
  onChange,
  value,
  buttons,
  style,
  align,
  size,
}: CustomToggleButtonProps) => {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={onChange}
      sx={{
        width: "100%",
        boxShadow: 2,
        gap: 0.25,
        ...style,
      }}
    >
      {buttons.map((button, idx) => (
        <ToolTip content={button.disabled && button.disabledInfo} key={idx}>
          {/* Extra stack because of posibility of button being disabled */}
          <Stack width={"100%"}>
            <ToggleButton
              id={button.id}
              size={size}
              onClick={(e) => onChange(e, button.value)}
              className={button.value === value ? "Mui-selected" : ""}
              key={button.value}
              value={button.value}
              sx={{
                borderTopLeftRadius: idx === 1 ? 0 : undefined,
                borderBottomLeftRadius: idx === 1 ? 0 : undefined,
                borderTopRightRadius: idx === 0 ? 0 : undefined,
                borderBottomRightRadius: idx === 0 ? 0 : undefined,
                border: "none",
                width: "100%",
                paddingY: size === "large" ? null : "6px",
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                "&.MuiToggleButton-standard": {
                  backgroundColor: "secondary.main",
                  "&:hover": {
                    backgroundColor: "secondary.dark",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#29405B",
                    "&:hover": {
                      backgroundColor: "#273B54",
                    },
                  },
                },
                "&.Mui-disabled": {
                  border: "none",
                },
              }}
              disabled={button.disabled}
            >
              {button.startIcon && (
                <Stack marginLeft={"-4px"} marginRight={"8px"}>
                  {button.startIcon}
                </Stack>
              )}
              {button.label && (
                <Grid
                  item
                  gridColumn={2}
                  textAlign={align === "left" ? "left" : "center"}
                >
                  {button.label}
                </Grid>
              )}
              {button.endIcon && !button.disabled && (
                <Stack>{button.endIcon}</Stack>
              )}
              {button.disabled && button.disabledInfo && (
                <Stack>
                  {
                    <InfoOutlined
                      sx={{
                        cursor: "pointer",
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  }
                </Stack>
              )}
            </ToggleButton>
          </Stack>
        </ToolTip>
      ))}
    </ToggleButtonGroup>
  );
};

CustomToggleButton.defaultProps = {};

export default CustomToggleButton;
